import { z } from 'zod';
import { isValidIsoDate } from '../lib/isoDate';
import {
  DEST_KEYS,
  GROUP_KEYS,
  MONTH_COUNT,
  PERMISSIONS,
  SCHEDULE_SLOTS,
  SEGMENT_KEYS,
  STANDARDIZATION_KEYS,
  TASK_GROUP_KEYS,
} from '../domain/constants';
import {
  birthDateIssue,
  DATE_FORMAT_MESSAGE,
  workStartDateIssue,
  workStartVsBirthIssue,
} from './staffDates';

/**
 * 接口契約。このファイルが「単一の事実源」で、docs/api/api-contract.md はここから生成される。
 * 方針: 構造(配列・必須キー)は厳格に、自由記述の値は緩く受けて mapper で正規化する。
 * 後端への約束: 空コレクションは [] を返す。値が無いことは null で表し 0 で代用しない。
 */

const monthIndex = z
  .number()
  .int()
  .min(0)
  .max(MONTH_COUNT - 1);

// ---------- cases ----------
export const CaseDtoSchema = z
  .object({
    id: z.string().min(1),
    name: z.string(),
    customer: z.string().nullish(),
    kind: z.string().nullish(),
    /** base | ext | grow。不明な値は null 扱い(mapper)。 */
    segment: z.string().nullish(),
    group: z.enum(GROUP_KEYS),
    startMonth: monthIndex,
    endMonth: monthIndex,
    /** 必要量(人工)。1人が1ヶ月フルに従事して 1.0。 */
    requiredNinku: z.number().min(0),
    status: z.string(),
    dedicatedAgent: z.boolean().nullish(),
    owner: z.string().nullish(),
  })
  .refine((c) => c.startMonth <= c.endMonth, { message: 'startMonth must be <= endMonth' });
export const CasesResponseSchema = z.object({ items: z.array(CaseDtoSchema) });
export type CasesResponse = z.infer<typeof CasesResponseSchema>;

// ---------- staff ----------
export const AllocationDtoSchema = z.object({
  caseId: z.string().min(1),
  month: monthIndex,
  /** その月の配分(%)。 */
  percent: z.number().min(0),
  role: z.string().nullish(),
});
/** サーバーが返す日付。実在する暦日の `YYYY-MM-DD` だけを通す(壊れた値は INVALID_RESPONSE)。欠落・null は許す。 */
const serverIsoDate = z.string().refine(isValidIsoDate, { message: DATE_FORMAT_MESSAGE });

export const StaffDtoSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  group: z.enum(GROUP_KEYS),
  /** 生年月日(`YYYY-MM-DD`)。不明なら null。 */
  birthDate: serverIsoDate.nullish(),
  /** 勤務開始日(`YYYY-MM-DD`)。不明なら null。 */
  workStartDate: serverIsoDate.nullish(),
  /** 満年齢。生年月日から計算した値(サーバーが返す)。不明なら null。 */
  age: z.number().nullish(),
  /** 経験年数(満年数)。勤務開始日から計算した値。不明なら null。 */
  yearsOfExperience: z.number().nullish(),
  canBeAgent: z.boolean().nullish(),
  hasDcExperience: z.boolean().nullish(),
  qualifications: z.array(z.string()).nullish(),
  base: z.string().nullish(),
  allocations: z.array(AllocationDtoSchema),
});
export const StaffResponseSchema = z.object({ items: z.array(StaffDtoSchema) });
export type StaffResponse = z.infer<typeof StaffResponseSchema>;

// ---------- tasks(業務マスタ) ----------
export const TaskGroupDtoSchema = z.object({
  priority1: z.union([z.string(), z.number()]).nullish(),
  priority2: z.union([z.string(), z.number()]).nullish(),
  now: z.string().nullish(),
  next: z.string().nullish(),
  standardization: z.string().nullish(),
  dx: z.string().nullish(),
  note: z.string().nullish(),
});
export type TaskGroupDto = z.infer<typeof TaskGroupDtoSchema>;

export const TaskDtoSchema = z.object({
  id: z.string().min(1),
  phase: z.string(),
  gyomu: z.string(),
  task: z.string(),
  issue: z.string().nullish(),
  hoursPerMonth: z.number().min(0).nullish(),
  groups: z.object({
    hq: TaskGroupDtoSchema.nullish(),
    sen: TaskGroupDtoSchema.nullish(),
    mu: TaskGroupDtoSchema.nullish(),
    ki: TaskGroupDtoSchema.nullish(),
  }),
  ideaNo: z.string().nullish(),
  ideaMemo: z.string().nullish(),
  ideas: z
    .array(
      z.object({
        text: z.string(),
        source: z.string().nullish(),
        target: z.string().nullish(),
      }),
    )
    .nullish(),
});
export const TasksResponseSchema = z.object({ items: z.array(TaskDtoSchema) });
export type TasksResponse = z.infer<typeof TasksResponseSchema>;

// ---------- schedule ----------
export const ScheduleRowDtoSchema = z.object({
  taskId: z.string().min(1),
  cells: z.array(
    z.object({
      index: z
        .number()
        .int()
        .min(0)
        .max(SCHEDULE_SLOTS - 1),
      stage: z.number().int().min(1).max(5),
    }),
  ),
});
export const MilestoneDtoSchema = z.object({ label: z.string(), text: z.string() });
export const WorkingGroupDtoSchema = z.object({
  key: z.enum(TASK_GROUP_KEYS),
  hqLead: z.string().nullish(),
  centerLead: z.string().nullish(),
  members: z.array(z.string()),
});
export const DxDtoSchema = z.object({ lead: z.string().nullish(), members: z.array(z.string()) });

export const ScheduleResponseSchema = z.object({
  rows: z.array(ScheduleRowDtoSchema),
  milestones: z.array(MilestoneDtoSchema),
  workingGroups: z.array(WorkingGroupDtoSchema),
  dx: DxDtoSchema.nullish(),
});
export type ScheduleResponse = z.infer<typeof ScheduleResponseSchema>;

// ---------- simulation ----------
const reflectionValue = z.number().nullish();
export const ReflectionsResponseSchema = z.object({
  reflections: z.object({
    sen: reflectionValue,
    mu: reflectionValue,
    ki: reflectionValue,
  }),
});
export type ReflectionsResponse = z.infer<typeof ReflectionsResponseSchema>;

export const ReflectRequestSchema = z.object({
  group: z.enum(TASK_GROUP_KEYS),
  ninku: z.number().min(0),
});
export type ReflectRequest = z.infer<typeof ReflectRequestSchema>;

// ---------- allocations ----------
export const AssignRequestSchema = z.object({
  staffId: z.string().min(1),
  caseId: z.string().min(1),
  percent: z.number().int().min(10).max(100),
  role: z.string().min(1),
});
export type AssignRequest = z.infer<typeof AssignRequestSchema>;

export const AssignResponseSchema = z.object({
  assignedMonths: z.array(monthIndex),
  totalPercent: z.number().min(0),
});
export type AssignResponse = z.infer<typeof AssignResponseSchema>;

// ================= 認証 =================
export const AuthUserDtoSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  /** 未知の権限文字列も受け取る(mapUser が既知のものだけに絞る)。 */
  permissions: z.array(z.string()),
});
export type AuthUserDto = z.infer<typeof AuthUserDtoSchema>;

export const LoginRequestSchema = z.object({
  id: z.string().trim().min(1, 'IDを入力してください').max(64),
  password: z.string().min(1, 'パスワードを入力してください').max(128),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const LoginResponseSchema = z.object({ token: z.string().min(1), user: AuthUserDtoSchema });
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const MeResponseSchema = z.object({ user: AuthUserDtoSchema });
export type MeResponse = z.infer<typeof MeResponseSchema>;

// ================= 書き込み(管理機能) =================
const REQUIRED = '必須項目です';
const requiredText = (max: number) =>
  z.string().trim().min(1, REQUIRED).max(max, `${max}文字以内で入力してください`);
const optionalText = (max: number) =>
  z.string().trim().max(max, `${max}文字以内で入力してください`).nullable();
/** 数値欄。空や数字でない入力は、Zod 既定の英語ではなく日本語で知らせる。 */
const numeric = () => z.number({ error: '数値で入力してください' });
/** 整数欄。小数は日本語で知らせる(Zod 既定は英語)。 */
const integer = () => numeric().int('整数で入力してください');

export const TaskGroupWriteSchema = z.object({
  priority1: integer().min(0, '0以上').max(99, '99以下').nullable(),
  priority2: integer().min(0, '0以上').max(99, '99以下').nullable(),
  now: z.enum(DEST_KEYS).nullable(),
  next: z.enum(DEST_KEYS).nullable(),
  standardization: z.enum(STANDARDIZATION_KEYS).nullable(),
  dx: optionalText(500),
  note: optionalText(1000),
});

export const TaskWriteSchema = z.object({
  phase: requiredText(100),
  gyomu: requiredText(100),
  task: requiredText(100),
  issue: optionalText(1000),
  hoursPerMonth: numeric().min(0, '0以上').max(160, '160以下').nullable(),
  groups: z.object({
    hq: TaskGroupWriteSchema,
    sen: TaskGroupWriteSchema,
    mu: TaskGroupWriteSchema,
    ki: TaskGroupWriteSchema,
  }),
  ideaNo: optionalText(20),
  ideaMemo: optionalText(1000),
  ideas: z
    .array(
      z.object({
        text: requiredText(200),
        source: optionalText(200),
        target: optionalText(100),
      }),
    )
    .max(20, '20件まで'),
});
export type TaskWrite = z.infer<typeof TaskWriteSchema>;

export const CaseWriteSchema = z
  .object({
    name: requiredText(100),
    customer: optionalText(100),
    kind: optionalText(100),
    segment: z.enum(SEGMENT_KEYS).nullable(),
    group: z.enum(GROUP_KEYS),
    startMonth: monthIndex,
    endMonth: monthIndex,
    requiredNinku: numeric().min(0, '0以上').max(999, '999以下'),
    status: requiredText(20),
    dedicatedAgent: z.boolean(),
  })
  .refine((c) => c.startMonth <= c.endMonth, {
    message: '終了月は開始月以降にしてください',
    path: ['endMonth'],
  });
export type CaseWrite = z.infer<typeof CaseWriteSchema>;

/** 日付欄(`YYYY-MM-DD` または null)。`check` の戻り値がメッセージなら、その欄のエラーにする。 */
const staffDateField = (check: (value: string) => string | null) =>
  z
    .string({ error: DATE_FORMAT_MESSAGE })
    .nullable()
    .superRefine((value, ctx) => {
      const message = value === null ? null : check(value);
      if (message !== null) ctx.addIssue({ code: 'custom', message });
    });

/**
 * 社員の書き込み本文。年齢・経験年数は送らず、生年月日と勤務開始日から計算する。
 * `today`(`YYYY-MM-DD`)を渡すと「未来でない」「年齢 15..80」「経験 60 年以内」も検証する。
 * 画面もモックサーバーも自分の時計を渡す。スキーマ自体は時計を読まない。
 */
export const makeStaffWriteSchema = (today: string | null = null) =>
  z
    .object({
      name: requiredText(100),
      group: z.enum(GROUP_KEYS),
      birthDate: staffDateField((v) => birthDateIssue(v, today)),
      workStartDate: staffDateField((v) => workStartDateIssue(v, today)),
      canBeAgent: z.boolean(),
      hasDcExperience: z.boolean(),
      qualifications: z.array(requiredText(60)).max(20, '20件まで'),
      base: optionalText(60),
    })
    .superRefine((body, ctx) => {
      const message = workStartVsBirthIssue(body.birthDate, body.workStartDate);
      if (message !== null) ctx.addIssue({ code: 'custom', message, path: ['workStartDate'] });
    });
/** 時計に依存しない規則だけの版。「今日」を使う検証は `makeStaffWriteSchema(today)` を使う。 */
export const StaffWriteSchema = makeStaffWriteSchema();
export type StaffWrite = z.infer<typeof StaffWriteSchema>;

export const ScheduleRowWriteSchema = z
  .object({ cells: ScheduleRowDtoSchema.shape.cells.max(SCHEDULE_SLOTS) })
  .refine((row) => new Set(row.cells.map((c) => c.index)).size === row.cells.length, {
    message: '同じ旬を二重に指定できません',
    path: ['cells'],
  });
export type ScheduleRowWrite = z.infer<typeof ScheduleRowWriteSchema>;

export const MilestonesWriteSchema = z.object({
  milestones: z
    .array(z.object({ label: requiredText(40), text: requiredText(300) }))
    .max(30, '30件まで'),
});
export type MilestonesWrite = z.infer<typeof MilestonesWriteSchema>;

const personName = z.string().trim().min(1, REQUIRED).max(60, '60文字以内で入力してください');
export const WorkingGroupsWriteSchema = z.object({
  workingGroups: z
    .array(
      z.object({
        key: z.enum(TASK_GROUP_KEYS),
        hqLead: optionalText(60),
        centerLead: optionalText(60),
        members: z.array(personName).max(30, '30人まで'),
      }),
    )
    .max(TASK_GROUP_KEYS.length),
  dx: z
    .object({ lead: optionalText(60), members: z.array(personName).max(30, '30人まで') })
    .nullable(),
});
export type WorkingGroupsWrite = z.infer<typeof WorkingGroupsWriteSchema>;

export const AllocationReplaceSchema = z
  .object({
    months: z
      .array(
        z.object({
          month: monthIndex,
          percent: integer().min(1, '1以上').max(300, '300以下'),
          role: requiredText(40),
        }),
      )
      .max(MONTH_COUNT),
  })
  .refine((body) => new Set(body.months.map((m) => m.month)).size === body.months.length, {
    message: '同じ月を二重に指定できません',
    path: ['months'],
  });
export type AllocationReplace = z.infer<typeof AllocationReplaceSchema>;

// ---------- アカウント管理 ----------
/** ログイン ID。サーバーと同じ規則(半角英数字と . _ - のみ、1〜64 文字)。 */
export const ACCOUNT_ID_PATTERN = /^[A-Za-z0-9._-]{1,64}$/;
export const MIN_PASSWORD_LENGTH = 12;
/**
 * パスワードの上限(UTF-8 のバイト数)。サーバーは bcrypt で保存し、72 バイトを超えると 500 になる
 * (実 BE で確認)。ログイン画面の上限(128 文字)より小さい。
 */
export const MAX_PASSWORD_BYTES = 72;
const utf8Bytes = (text: string): number => new TextEncoder().encode(text).length;
const PASSWORD_TOO_LONG = `${MAX_PASSWORD_BYTES}バイト以内で入力してください(半角英数字なら ${MAX_PASSWORD_BYTES} 文字)`;

const accountId = z
  .string()
  .regex(ACCOUNT_ID_PATTERN, '半角英数字と . _ - のみ、64文字以内で入力してください');
const accountPermissions = z
  .array(z.enum(PERMISSIONS))
  .refine((list) => new Set(list).size === list.length, { message: '権限が重複しています' });
const SAME_AS_ID = 'ログイン ID と同じパスワードは使えません';

/** パスワード。ログイン ID と同じ値は不可(`loginId` を渡す)。 */
const passwordFor = (loginId: string) =>
  z
    .string()
    .min(MIN_PASSWORD_LENGTH, `${MIN_PASSWORD_LENGTH}文字以上で入力してください`)
    .refine((value) => utf8Bytes(value) <= MAX_PASSWORD_BYTES, { message: PASSWORD_TOO_LONG })
    .refine((value) => value !== loginId, { message: SAME_AS_ID });

export const AccountCreateSchema = z
  .object({
    id: accountId,
    name: requiredText(100),
    permissions: accountPermissions,
    password: z
      .string()
      .min(MIN_PASSWORD_LENGTH, `${MIN_PASSWORD_LENGTH}文字以上で入力してください`)
      .refine((value) => utf8Bytes(value) <= MAX_PASSWORD_BYTES, { message: PASSWORD_TOO_LONG }),
  })
  .superRefine((body, ctx) => {
    if (body.password === body.id) {
      ctx.addIssue({ code: 'custom', message: SAME_AS_ID, path: ['password'] });
    }
  });
export type AccountCreate = z.infer<typeof AccountCreateSchema>;

/** 更新。ログイン ID は変えられない(パスで指す)。 */
export const AccountUpdateSchema = z.object({
  name: requiredText(100),
  permissions: accountPermissions,
  enabled: z.boolean(),
});
export type AccountUpdate = z.infer<typeof AccountUpdateSchema>;

/** パスワード再設定。ログイン ID と同じ値を弾くため、対象の ID を渡して作る。 */
export const makePasswordResetSchema = (loginId: string) =>
  z.object({ password: passwordFor(loginId) });
export type PasswordReset = z.infer<ReturnType<typeof makePasswordResetSchema>>;

// ================= 書き込みの応答 =================
export const TaskItemResponseSchema = z.object({ item: TaskDtoSchema });
export const CaseItemResponseSchema = z.object({
  item: CaseDtoSchema,
  /** 期間の変更で自動的に削除した配置(月単位)の件数 */
  trimmedAllocations: z.number().int().min(0).nullish(),
});
export const StaffItemResponseSchema = z.object({ item: StaffDtoSchema });
/** 削除系の応答。件数は必須(欠けた・形が違う応答を成功とみなさない)。エンドポイントごとに別の項目。 */
const removedCount = z.number().int().min(0);
export const TaskRemovedResponseSchema = z.object({ removedScheduleRows: removedCount });
export const CaseRemovedResponseSchema = z.object({ removedAllocations: removedCount });
export const StaffRemovedResponseSchema = z.object({ removedAllocations: removedCount });
export const AllocationRemovedResponseSchema = z.object({ removedMonths: removedCount });

// アカウント。パスワード(ハッシュを含む)は応答に含まれない。
export const AccountDtoSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  /** 未知の権限文字列も受け取る(mapAccount が既知のものだけに絞る)。 */
  permissions: z.array(z.string()),
  /** 欠けたら有効として扱う(mapper)。 */
  enabled: z.boolean().nullish(),
});
export const AccountsResponseSchema = z.object({ items: z.array(AccountDtoSchema) });
export const AccountItemResponseSchema = z.object({ item: AccountDtoSchema });
export const AccountRemovedResponseSchema = z.object({ removed: removedCount });
/** パスワード再設定の応答は空オブジェクト。 */
export const AccountPasswordResponseSchema = z.object({});
export type AccountDto = z.infer<typeof AccountDtoSchema>;
export type AccountsResponse = z.infer<typeof AccountsResponseSchema>;
export type AccountItemResponse = z.infer<typeof AccountItemResponseSchema>;
export type AccountRemovedResponse = z.infer<typeof AccountRemovedResponseSchema>;

export const ScheduleRowResponseSchema = z.object({ item: ScheduleRowDtoSchema });
export const MilestonesResponseSchema = z.object({ milestones: z.array(MilestoneDtoSchema) });
export const WorkingGroupsResponseSchema = z.object({
  workingGroups: z.array(WorkingGroupDtoSchema),
  dx: DxDtoSchema.nullish(),
});
export const AllocationReplaceResponseSchema = z.object({
  months: z.array(z.object({ month: monthIndex, percent: z.number(), role: z.string().nullish() })),
});
export type TaskItemResponse = z.infer<typeof TaskItemResponseSchema>;
export type CaseItemResponse = z.infer<typeof CaseItemResponseSchema>;
export type StaffItemResponse = z.infer<typeof StaffItemResponseSchema>;
export type TaskRemovedResponse = z.infer<typeof TaskRemovedResponseSchema>;
export type CaseRemovedResponse = z.infer<typeof CaseRemovedResponseSchema>;
export type StaffRemovedResponse = z.infer<typeof StaffRemovedResponseSchema>;
export type AllocationRemovedResponse = z.infer<typeof AllocationRemovedResponseSchema>;
export type ScheduleRowResponse = z.infer<typeof ScheduleRowResponseSchema>;
export type MilestonesResponse = z.infer<typeof MilestonesResponseSchema>;
export type WorkingGroupsResponse = z.infer<typeof WorkingGroupsResponseSchema>;
export type AllocationReplaceResponse = z.infer<typeof AllocationReplaceResponseSchema>;
